import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    Card,
    CardContent,
    CardActionArea,
    Grid,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { Add as AddIcon, CalendarToday as CalendarIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useSessionStudio } from './SessionContext';
import { getAllSessions, deleteSession, Session } from 'services/sessionService';
import SessionHeader from './SessionHeader';
import SessionGroupCard from './SessionGroupCard';
import NewSessionDialog, { NewSessionData } from './NewSessionDialog';
import { useNavigate } from 'react-router-dom';

const SessionCanvas: React.FC = () => {
    const navigate = useNavigate();
    const { selectedSessionId, sessionData, problemGroups, isLoading, addProblemGroup, updateProblemGroup, refreshSession, createNewSession, activeSessionId } = useSessionStudio();
    const [allSessions, setAllSessions] = useState<Session[]>([]);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [newSessionDialogOpen, setNewSessionDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [sessionToDelete, setSessionToDelete] = useState<Session | null>(null);
    const [deleting, setDeleting] = useState(false);

    const handleAddGroup = async () => {
        if (!selectedSessionId) return;
        try {
            await addProblemGroup(selectedSessionId);
        } catch (error) {
            console.error('Failed to add group:', error);
        }
    };

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination) return;

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex) return;

        // Optimistically reorder in UI
        const reorderedGroups = Array.from(problemGroups);
        const [removed] = reorderedGroups.splice(sourceIndex, 1);
        reorderedGroups.splice(destinationIndex, 0, removed);

        // Update group_order for each affected group
        try {
            const updatePromises = reorderedGroups.map((group, index) =>
                updateProblemGroup(group.id, { groupOrder: index + 1 })
            );
            await Promise.all(updatePromises);
            await refreshSession();
        } catch (error) {
            console.error('Failed to reorder groups:', error);
            await refreshSession(); // Revert by refreshing
        }
    };

    // Fetch all sessions when no session is selected
    useEffect(() => {
        if (!selectedSessionId) {
            const fetchSessions = async () => {
                setLoadingSessions(true);
                try {
                    const sessions = await getAllSessions();
                    const sorted = sessions.sort((a, b) =>
                        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                    );
                    setAllSessions(sorted);
                } catch (error) {
                    console.error('Error fetching sessions:', error);
                } finally {
                    setLoadingSessions(false);
                }
            };
            fetchSessions();
        }
    }, [selectedSessionId]);

    const handleCreateNew = async (data: NewSessionData) => {
        const newSession = await createNewSession(data.title, data.description, data.startDate, data.endDate);
        navigate(`/admin/sessions/${newSession.id}`);
    };

    const handleDeleteClick = (e: React.MouseEvent, session: Session) => {
        e.stopPropagation();
        setSessionToDelete(session);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!sessionToDelete) return;

        setDeleting(true);
        try {
            await deleteSession(sessionToDelete.id);
            setAllSessions(prev => prev.filter(s => s.id !== sessionToDelete.id));
            setDeleteDialogOpen(false);
            setSessionToDelete(null);
        } catch (error) {
            console.error('Error deleting session:', error);
        } finally {
            setDeleting(false);
        }
    };

    if (!selectedSessionId) {
        return (
            <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                        <Typography variant="h4" fontWeight="bold" gutterBottom>
                            Sessions
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Create and manage your math competition sessions
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setNewSessionDialogOpen(true)}
                        disabled={loadingSessions}
                    >
                        New Session
                    </Button>
                </Box>

                {loadingSessions ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={2} data-demo-id="admin-sessions-list">
                        {allSessions.map((session) => (
                            <Grid item xs={12} sm={6} md={4} key={session.id}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: '100%',
                                        transition: 'all 0.2s',
                                        position: 'relative',
                                        '&:hover': {
                                            boxShadow: 3,
                                            transform: 'translateY(-2px)'
                                        },
                                        '&:hover .delete-btn': {
                                            opacity: 1
                                        }
                                    }}
                                >
                                    <IconButton
                                        className="delete-btn"
                                        size="small"
                                        color="error"
                                        onClick={(e) => handleDeleteClick(e, session)}
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            bgcolor: 'background.paper',
                                            '&:hover': { bgcolor: 'error.light', color: 'white' },
                                            zIndex: 1
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                    <CardActionArea onClick={() => navigate(`/admin/sessions/${session.id}`)}>
                                        <CardContent>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                                    {session.title}
                                                </Typography>
                                                {session.id === activeSessionId && (
                                                    <Chip label="Active" color="success" size="small" sx={{ mb: 1 }} />
                                                )}
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {session.description}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <CalendarIcon fontSize="small" color="action" />
                                                <Typography variant="caption" color="text.secondary">
                                                    {new Date(session.startDate).toLocaleDateString()} - {new Date(session.endDate).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                                <Chip
                                                    label={session.acceptingEnrollment ? "Open" : "Closed"}
                                                    size="small"
                                                    color={session.acceptingEnrollment ? "success" : "default"}
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    label={`$${session.basePrice}`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}

                {/* New Session Dialog */}
                <NewSessionDialog
                    open={newSessionDialogOpen}
                    onClose={() => setNewSessionDialogOpen(false)}
                    onSubmit={handleCreateNew}
                />

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
                    <DialogTitle>Delete Session</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete "{sessionToDelete?.title}"? This will permanently remove the session and all its problems, groups, and enrollments. This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            color="error"
                            variant="contained"
                            disabled={deleting}
                            startIcon={deleting ? <CircularProgress size={20} /> : null}
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!sessionData) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="error">Failed to load session data.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            maxWidth: 900,
            mx: 'auto',
            p: 4,
            pb: 20 // Extra padding at bottom for scrolling
        }}>
            {/* Session Header (Title, Dates, etc.) */}
            <SessionHeader session={sessionData} />

            {/* Problem Groups List */}
            <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" color="text.secondary">
                        Content ({problemGroups.length} Groups)
                    </Typography>
                    <Button startIcon={<AddIcon />} onClick={handleAddGroup}>
                        Add Week
                    </Button>
                </Box>

                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="session-groups">
                        {(provided) => (
                            <Box
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                            >
                                {problemGroups.length === 0 ? (
                                    <Box sx={{
                                        p: 4,
                                        border: '2px dashed',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        textAlign: 'center',
                                        bgcolor: 'background.paper'
                                    }}>
                                        <Typography color="text.secondary" gutterBottom>
                                            This session has no content yet.
                                        </Typography>
                                        <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={handleAddGroup}>
                                            Create First Week
                                        </Button>
                                    </Box>
                                ) : (
                                    problemGroups.map((group, index) => (
                                        <Draggable key={group.id} draggableId={group.id.toString()} index={index}>
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                >
                                                    <SessionGroupCard group={group} dragHandleProps={provided.dragHandleProps} />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))
                                )}
                                {provided.placeholder}
                            </Box>
                        )}
                    </Droppable>
                </DragDropContext>
            </Box>
        </Box>
    );
};

export default SessionCanvas;
